import { useState, useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './LightModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions
// ---------------------------------------------------------------------------

export const LIGHT_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  { id: 'power', label: 'Power', icon: 'power_settings_new', slots: [
    { id: 'power-label', label: 'Title "POWER STATE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'power-value', label: 'Status ON/OFF', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn', label: 'Power Button', editableProps: ['background', 'color', 'borderRadius', 'width', 'height', 'boxShadow'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn-icon', label: 'Power Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
  ]},
  { id: 'brightness', label: 'Brightness', icon: 'light_mode', slots: [
    { id: 'brightness-label', label: 'Title "BRIGHTNESS"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'brightness-value', label: 'Value "%"', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'brightness-track', label: 'Slider Track', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'brightness-thumb', label: 'Slider Thumb', editableProps: ['background', 'borderRadius', 'width', 'height', 'boxShadow'] },
  ]},
  { id: 'colortemp', label: 'Color Temperature', icon: 'thermostat', slots: [
    { id: 'colortemp-label', label: 'Title "COLOR TEMPERATURE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'colortemp-value', label: 'Value "K"', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'colortemp-track', label: 'Slider Track', editableProps: ['borderRadius', 'height'] },
    { id: 'colortemp-thumb', label: 'Slider Thumb', editableProps: ['background', 'borderRadius', 'width', 'height', 'boxShadow'] },
  ]},
  { id: 'presets', label: 'Color Presets', icon: 'palette', slots: [
    { id: 'preset-label', label: 'Title "PRESETS"', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'preset-btn', label: 'Preset Button', editableProps: ['borderRadius', 'width', 'height', 'border', 'boxShadow'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
]

const DEFAULT_SECTION_ORDER = LIGHT_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const k of keys) if (state[k] !== undefined) return state[k]
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  return typeof v === 'number' ? v : v !== undefined ? Number(v) || null : null
}

const COLOR_PRESETS = [
  { id: 'warm', temp: 2700, color: '#ffb347' },
  { id: 'soft', temp: 3000, color: '#ffd699' },
  { id: 'neutral', temp: 4000, color: '#fff5e6' },
  { id: 'cool', temp: 5000, color: '#e6f0ff' },
  { id: 'daylight', temp: 6500, color: '#cce0ff' },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function LightModalClassic({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  highlightedSection,
  onSectionContextMenu,
}: DeviceModalTemplateProps) {
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])
  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  const isOn = getAttr(deviceState, 'on', 'isOn', 'powerState') === true
    || String(getAttr(deviceState, 'on', 'isOn', 'powerState')) === 'true'
  const brightness = numAttr(deviceState, 'brightness', 'currentBrightness') ?? 100
  const colorTemp = numAttr(deviceState, 'colorTemperatureKelvin', 'colorTemp', 'color_temperature') ?? 4000

  const [pendingBrightness, setPendingBrightness] = useState<number | null>(null)
  const [pendingColorTemp, setPendingColorTemp] = useState<number | null>(null)
  const displayBrightness = pendingBrightness ?? brightness
  const displayColorTemp = pendingColorTemp ?? colorTemp

  const togglePower = useCallback(() => {
    if (isEditorMode) return
    onCommand(isOn ? 'off' : 'on')
  }, [isEditorMode, isOn, onCommand])

  const setBrightness = useCallback((val: number) => {
    if (isEditorMode) return
    const clamped = Math.max(0, Math.min(100, val))
    setPendingBrightness(clamped)
    onCommand('setBrightness', { brightness: clamped })
  }, [isEditorMode, onCommand])

  const setColorTemp = useCallback((val: number) => {
    if (isEditorMode) return
    const clamped = Math.max(2700, Math.min(6500, val))
    setPendingColorTemp(clamped)
    onCommand('setColorTemperature', { temperature: clamped })
  }, [isEditorMode, onCommand])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!isEditorMode || !onSectionContextMenu) return
    const section = (e.target as HTMLElement).closest('[data-modal-section]')
    if (!section) return
    e.preventDefault()
    onSectionContextMenu(section.getAttribute('data-modal-section')!, { clientX: e.clientX, clientY: e.clientY })
  }, [isEditorMode, onSectionContextMenu])

  return (
    <div
      className={`light-modal-classic${isOn ? '' : ' is-off'}${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="light-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />}

      {/* Header */}
      <div className="light-modal-classic__header">
        <button className="light-modal-classic__back" onClick={onClose}>
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="light-modal-classic__title" data-modal-slot="header-title">{device.name || 'Light'}</h2>
        <button className="light-modal-classic__edit">
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'power':
            return (
              <div key="power" className={`light-modal-classic__power${hl}`} data-modal-section="power">
                <div className="light-modal-classic__power-label" data-modal-slot="power-label">POWER STATE</div>
                <div className="light-modal-classic__power-row">
                  <span className={`light-modal-classic__power-value${isOn ? ' on' : ' off'}`} data-modal-slot="power-value" data-modal-slot-state={isOn ? 'on' : 'off'}>
                    {isOn ? 'ON' : 'OFF'}
                  </span>
                  <button className={`light-modal-classic__power-btn${isOn ? ' on' : ''}`} data-modal-slot="power-btn" data-modal-slot-state={isOn ? 'on' : 'off'} onClick={togglePower}>
                    <span className="material-symbols-rounded" data-modal-slot="power-btn-icon" data-modal-slot-state={isOn ? 'on' : 'off'}>power_settings_new</span>
                  </button>
                </div>
              </div>
            )
          case 'brightness':
            return (
              <div key="brightness" className={`light-modal-classic__brightness${hl}`} data-modal-section="brightness">
                <div className="light-modal-classic__slider-header">
                  <span data-modal-slot="brightness-label">BRIGHTNESS</span>
                  <span data-modal-slot="brightness-value">{displayBrightness}%</span>
                </div>
                <div className="light-modal-classic__slider-wrap">
                  <input
                    type="range" min="0" max="100"
                    value={displayBrightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                    className="light-modal-classic__slider brightness-slider"
                    data-modal-slot="brightness-track"
                  />
                </div>
              </div>
            )
          case 'colortemp':
            return (
              <div key="colortemp" className={`light-modal-classic__colortemp${hl}`} data-modal-section="colortemp">
                <div className="light-modal-classic__slider-header">
                  <span data-modal-slot="colortemp-label">COLOR TEMPERATURE</span>
                  <span data-modal-slot="colortemp-value">{displayColorTemp}K</span>
                </div>
                <div className="light-modal-classic__slider-wrap">
                  <input
                    type="range" min="2700" max="6500" step="100"
                    value={displayColorTemp}
                    onChange={(e) => setColorTemp(Number(e.target.value))}
                    className="light-modal-classic__slider colortemp-slider"
                    data-modal-slot="colortemp-track"
                  />
                </div>
              </div>
            )
          case 'presets':
            return (
              <div key="presets" className={`light-modal-classic__presets${hl}`} data-modal-section="presets">
                <div data-modal-slot="preset-label" className="light-modal-classic__preset-label">PRESETS</div>
                <div className="light-modal-classic__preset-grid">
                  {COLOR_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      className={`light-modal-classic__preset-btn${displayColorTemp === p.temp ? ' active' : ''}`}
                      data-modal-slot="preset-btn"
                      data-modal-slot-state={displayColorTemp === p.temp ? 'active' : 'default'}
                      style={{ background: p.color }}
                      onClick={() => setColorTemp(p.temp)}
                    />
                  ))}
                </div>
              </div>
            )
          default: return null
        }
      })}
    </div>
  )
}
